import { css } from '@emotion/react';
import { useCallback, useRef } from 'react';
import { colors } from '../../../../../theme/colors';

const handleStyle = css`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 16px;
  height: 16px;
  cursor: se-resize;
  z-index: 10;
  touch-action: none;

  &::after {
    content: '';
    position: absolute;
    bottom: 3px;
    right: 3px;
    width: 8px;
    height: 8px;
    border-right: 2px solid ${colors.neutral[40]};
    border-bottom: 2px solid ${colors.neutral[40]};
  }

  &:hover::after {
    border-color: ${colors.primary[50]};
  }
`;

interface ResizeHandleProps {
  onResizeStart: () => void;
  onResize: (deltaX: number, deltaY: number) => void;
  onResizeEnd: (deltaX: number, deltaY: number) => void;
}

export function ResizeHandle({ onResizeStart, onResize, onResizeEnd }: ResizeHandleProps) {
  const startPosRef = useRef<{ x: number; y: number } | null>(null);
  const elementRef = useRef<HTMLDivElement>(null);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();

      // Capture pointer to this element - prevents @dnd-kit from seeing events
      e.currentTarget.setPointerCapture(e.pointerId);

      startPosRef.current = { x: e.clientX, y: e.clientY };
      onResizeStart();
    },
    [onResizeStart]
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!startPosRef.current) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();

      const deltaX = e.clientX - startPosRef.current.x;
      const deltaY = e.clientY - startPosRef.current.y;
      onResize(deltaX, deltaY);
    },
    [onResize]
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!startPosRef.current) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();

      // Release pointer capture
      e.currentTarget.releasePointerCapture(e.pointerId);

      const deltaX = e.clientX - startPosRef.current.x;
      const deltaY = e.clientY - startPosRef.current.y;
      onResizeEnd(deltaX, deltaY);
      startPosRef.current = null;
    },
    [onResizeEnd]
  );

  return (
    <div
      ref={elementRef}
      css={handleStyle}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
    />
  );
}
